/**
 * Order Model - Handles API response processing for Order module
 */

/**
 * Process order list API response
 * @param {Object} response - API response
 * @returns {Object} Processed order list data
 */
export const orderListGet = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Invalid response format',
      data: [],
      total: 0
    };
  }

  try {
    const { data } = response;

    if (!data.success) {
      return {
        success: false,
        message: data.message || 'Failed to fetch orders',
        data: [],
        total: 0
      };
    }

    return {
      success: true,
      data: data.data.map(order => ({
        id: order.id,
        slack: order.slack,
        order_merged: order.order_merged,
        order_number: order.order_number,
        customer_name: order.customer_name || 'Walk-in Customer',
        customer_phone: order.customer_phone || '',
        customer_email: order.customer_email || '',
        status: order.status,
        store_id: order.store_id,
        status_label: getOrderStatusLabel(order.status),
        payment_status: order.payment_status,
        payment_status_label: getPaymentStatusLabel(order.payment_status),
        total_amount: order.total_order_amount || 0,
        total_order_amount_rounded: order.total_order_amount_rounded || 0,
        createdAt: order.createdAt,
        createdBy: order.CreatedUser,
        updatedAt: order.updatedAt,
        status_data: order.status_data,
        createdUser: order.CreatedUser,
        updatedUser: order.UpdatedUser,
        payment_method: order.payment_method,
        pms_settlement_status: order.pms_settlement_status,
        acknowledgement_id: order.acknowledgement_id,
        pms_meta_data: order?.pms_meta_data,
        order_origin: order?.order_origin,
        store_data: order?.store || [],
        split_id: order?.split_id,
        parent_order_id: order?.parent_order_id,
        bill_number: order?.bill_number,
        room_number: order?.room_number,
        order_type: order?.order_type || '',
        business_day: order?.business_day || '',
        has_multiple_payments	:order?.has_multiple_payments	|| 0
      })),
      total: data.total || 0,
      message: ''
    };
  } catch (error) {
    console.error('Error processing order list:', error);
    return {
      success: false,
      message: 'Error processing order data',
      data: [],
      total: 0
    };
  }
};

/**
 * Process order details API response
 * @param {Object} response - API response
 * @returns {Object} Processed order details
 */
export const orderDetailsGet = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Invalid response format',
      data: null
    };
  }

  try {
    const { data } = response;

    if (!data.success) {
      return {
        success: false,
        message: data.message || 'Failed to fetch order details',
        data: null
      };
    }

    const orderData = data.data;

    return {
      success: true,
      data: {
        id: orderData.id,
        slack: orderData.slack,
        order_number: orderData.order_number,
        store_id: orderData.store_id,
        store: orderData.store || null,
        customer: {
          name: orderData.customer_name || 'Walk-in Customer(B2C)',
          phone: orderData.customer_phone || '',
          email: orderData.customer_email || '',
          address: orderData.address || '',
          id: orderData.customer_id || '',
        },
        contact_number: orderData.contact_number,
        register_id: orderData.register_id,
        store_level_discount_code_id: orderData.store_level_discount_code_id,
        store_level_discount_code: orderData.store_level_discount_code,
        store_level_total_discount_percentage: orderData.store_level_total_discount_percentage,
        store_level_total_discount_amount: orderData.store_level_total_discount_amount,
        product_level_total_discount_amount: orderData.product_level_total_discount_amount,
        store_level_tax_code_id: orderData.store_level_tax_code_id,
        store_level_tax_code: orderData.store_level_tax_code,
        store_level_total_tax_percentage: orderData.store_level_total_tax_percentage,
        store_level_total_tax_amount: orderData.store_level_total_tax_amount,
        store_level_total_tax_components: orderData.store_level_total_tax_components,
        product_level_total_tax_amount: orderData.product_level_total_tax_amount,
        purchase_amount_subtotal_excluding_tax: orderData.purchase_amount_subtotal_excluding_tax,
        sale_amount_subtotal_excluding_tax: orderData.sale_amount_subtotal_excluding_tax,
        total_discount_before_additional_discount: orderData.total_discount_before_additional_discount,
        total_amount_before_additional_discount: orderData.total_amount_before_additional_discount,
        additional_discount_percentage: orderData.additional_discount_percentage,
        additional_discount_amount: orderData.additional_discount_amount,
        total_discount_amount: orderData.total_discount_amount,
        total_after_discount: orderData.total_after_discount,
        total_tax_amount: orderData.total_tax_amount,
        total_order_amount: orderData.total_order_amount,
        total_order_amount_rounded: orderData.total_order_amount_rounded,
        payment_method_id: orderData.payment_method_id,
        payment_method_slack: orderData.payment_method_slack,
        payment_method: orderData.payment_method || '',
        currency_name: orderData.currency_name,
        currency_code: orderData.currency_code,
        business_account_id: orderData.business_account_id,
        order_type_id: orderData.order_type_id,
        order_ticket_type: orderData.order_ticket_type,
        order_type: orderData.order_type,
        restaurant_mode: orderData.restaurant_mode,
        bill_type_id: orderData.bill_type_id,
        bill_type: orderData.bill_type,
        // table_id: orderData.table_id,
        table_number: orderData.table_number,
        // waiter_id: orderData.waiter_id,
        order_origin: orderData.order_origin,
        status: orderData.status,
        status_label: getOrderStatusLabel(orderData.status),
        status_name:orderData?.status_data?.label,
        // kitchen_status: orderData.kitchen_status,
        kitchen_status: orderData.kitchen_status_data,
        payment_status: orderData.payment_status,
        // payment_status_label: getPaymentStatusLabel(orderData.payment_status),
        payment_status_label: orderData.payment_status_data,
        order_merged: orderData.order_merged,
        order_merge_parent_id: orderData.order_merge_parent_id,
        kitchen_screen_dismissed: orderData.kitchen_screen_dismissed,
        waiter_screen_dismissed: orderData.waiter_screen_dismissed,
        notes: orderData.notes || '',
        created_at: orderData.createdAt,
        updated_at: orderData.updatedAt,
        created_by: orderData.created_by?.fullname || 'System',
        updated_by: orderData.updated_by?.fullname || 'System',

        company_name: orderData?.company_name || '',
        company_gstin: orderData?.company_gstin || '',
        company_email: orderData?.company_email || '',
        company_address: orderData?.company_address || ''
        ,
        CreatedUser: {
          first_name: orderData.CreatedUser?.first_name || null,
          last_name: orderData.CreatedUser?.last_name || null
        },
        UpdatedUser: {
          first_name: orderData.UpdatedUser?.first_name || null,
          last_name: orderData.UpdatedUser?.last_name || null
        },
        merge_order: Array.isArray(orderData.merge_order)
          ? orderData.merge_order.map(m => ({
            order_number: m.order_number,
            slack: m.slack
          }))
          : [],
        order_products: Array.isArray(orderData.order_products) ? orderData.order_products.map(item => ({
          id: item.id,
          slack: item.slack,
          product_code: item.product_code,
          product_slack: item.product_slack,
          product_name: item.name,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.sale_amount_excluding_tax),
          discount_percentage: parseFloat(item.discount_percentage) || 0,
          discount_amount: parseFloat(item.discount_amount) || 0,
          tax_amount: parseFloat(item.total_tax_amount) || 0,
          tax_percentage: parseFloat(item.tax_percentage) || 0,
          tax_code: item.tax_code,
          tax_components: JSON.parse(item.tax_components),
          total_amount: parseFloat(item.total_amount),
          notes: item.notes || '',
          no_charge: item?.no_charge || "",
          no_charge_reason: item?.no_charge_reason || "",
          addon_items: Array.isArray(item.addon_products) ? item.addon_products.map(addon => ({
            id: addon.id,
            slack: addon.slack,
            product_code: addon.product_code,
            product_slack: addon.product_slack,
            product_name: addon.name,
            quantity: parseFloat(addon.quantity),
            unit_price: parseFloat(addon.sale_amount_excluding_tax),
            total_amount: parseFloat(addon.total_amount),
          })) : [],
          meta_data: item?.product?.meta_data ? item?.product?.meta_data : [],
          Category: item?.product?.Category ? item?.product?.Category : null,
          total_after_discount: parseFloat(item.total_after_discount),
          sub_total_sale_price_excluding_tax: parseFloat(item.sub_total_sale_price_excluding_tax),
          parent_order_product_id: item?.parent_order_product_id || ""

        })) : [],
        transactions: Array.isArray(orderData.transactions) ? orderData.transactions.map(trx => ({
          slack: trx.slack,
          code: trx.transaction_code || '-',
          date: trx.transaction_date || null,
          type: trx.type || '-',
          account: trx.account || '-',
          payment_method: trx.payment_method || '-',
          amount: parseFloat(trx.amount) || 0,
          created_at: trx.createdAt || null,
          created_by: trx.created_by || '-',
          label: trx.transaction_type_data.label,
          CreatedUser: trx.CreatedUser || null,
          is_bill_posting:
            trx.payment_method?.toLowerCase() === "bill posting" &&
            trx.payment_method_id === 0
              ? 1
              : 0,
          payment_status: trx?.payment_status
        })) : [],
        table: orderData.table || null,
        waiter: orderData.waiter || null,
        meta_data: orderData.meta_data ? orderData.meta_data : null,
        bill_pdf_url: orderData?.bill_pdf_url || "",
        invoice_pdf_url: orderData?.invoice_pdf_url || "",
        acknowledgement_id: orderData?.acknowledgement_id || "",
        pms_settlement_status: orderData?.pms_settlement_status || "",
        has_multiple_payments	:orderData?.has_multiple_payments	|| 0,
        pms_meta_data: orderData?.pms_meta_data || {},
        kot_masters: orderData?.kot_masters || [],
        invoice: orderData?.invoice || [],
        bill_number: orderData?.bill_number || "",
        service_charge_type: orderData?.service_charge_type || "",
        service_charge_amount: orderData?.service_charge_amount || "",
        service_charge_tax_amount: orderData?.service_charge_tax_amount || "",
        service_charge_value: orderData?.service_charge_value || "",
        tip_amount: orderData?.tip_amount || "",
        tip_value: orderData?.tip_value || "",
        tip_type: orderData?.tip_type || "",
        amount_excluding_tip: orderData?.amount_excluding_tip || "",
        room_number: orderData?.room_number || ""
      },
      message: ''
    };
  } catch (error) {
    console.error('Error processing order details:', error);
    return {
      success: false,
      message: 'Error processing order details',
      data: null
    };
  }
};

/**
 * Process create order API response
 * @param {Object} response - API response
 * @returns {Object} Processed create order response
 */
export const orderCreatePost = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Invalid response format',
      data: null
    };
  }

  try {
    const { data } = response;

    if (!data.success) {
      return {
        success: false,
        message: data.message || 'Failed to create order',
        data: null
      };
    }

    return {
      success: true,
      message: data.message || 'Order created successfully',
      data: {
        id: data.data.id,
        slack: data.data.slack,
        order_number: data.data.order_number,
        kot_number: data.data?.kot_print_details?.kot_no || "",
        bill_number: data.data?.bill_number || ""
      }
    };
  } catch (error) {
    console.error('Error processing create order response:', error);
    return {
      success: false,
      message: 'Error processing create order response',
      data: null
    };
  }
};

/**
 * Process update order API response
 * @param {Object} response - API response
 * @returns {Object} Processed update order response
 */
export const orderUpdatePut = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Invalid response format',
      data: null
    };
  }

  try {
    const { data } = response;

    if (!data.success) {
      return {
        success: false,
        message: data.message || 'Failed to update order',
        data: null
      };
    }

    return {
      success: true,
      message: data.message || 'Order updated successfully',
      data: data.data
    };
  } catch (error) {
    console.error('Error processing update order response:', error);
    return {
      success: false,
      message: 'Error processing update order response',
      data: null
    };
  }
};

/**
 * Process delete order API response
 * @param {Object} response - API response
 * @returns {Object} Processed delete order response
 */
export const orderDeleteDel = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Invalid response format'
    };
  }

  try {
    const { data } = response;

    if (!data.success) {
      return {
        success: false,
        message: data.message || 'Failed to delete order'
      };
    }

    return {
      success: true,
      message: data.message || 'Order deleted successfully'
    };
  } catch (error) {
    console.error('Error processing delete order response:', error);
    return {
      success: false,
      message: 'Error processing delete order response'
    };
  }
};

/**
 * Get order status label based on status code
 * @param {number} status - Status code
 * @returns {string} Status label
 */
const getOrderStatusLabel = (status) => {
  switch (parseInt(status)) {
    case 1:
      return 'New';
    case 2:
      return 'In Progress';
    case 3:
      return 'Ready';
    case 4:
      return 'Completed';
    case 5:
      return 'Cancelled';
    default:
      return 'Unknown';
  }
};

/**
 * Get payment status label based on status code
 * @param {number} status - Payment status code
 * @returns {string} Payment status label
 */
const getPaymentStatusLabel = (status) => {
  switch (parseInt(status)) {
    case 1:
      return 'Pending';
    case 2:
      return 'Paid';
    case 3:
      return 'Partially Paid';
    case 4:
      return 'Refunded';
    default:
      return 'Unknown';
  }
};

/**
 * Process payment method list response data
 * @param {Object} response - API response from payment method list endpoint
 * @returns {Object} Formatted payment method list data
 */
export const paymentMethodListGet = (response) => {
  // Based on Postman collection response format
  if (response && response.data && response.data.success === true) {
    // Map the API response to our frontend model
    const paymentMethods = Array.isArray(response.data.data) ? response.data.data.map(method => ({
      id: method.id,
      slack: method.slack,
      payment_method_name: method.label,
      description: method.description,
      status: method.status,
      status_data: method.status_data ? {
        id: method.status_data.id,
        value: method.status_data.value,
        label: method.status_data.label
      } : null,
      createdAt: method.createdAt,
      updatedAt: method.updatedAt,
      created_by: method.created_by,
      updated_by: method.updated_by,
      created_by_name: method.created_user?.fullname || null,
      updated_by_name: method.updated_user?.fullname || null
    })) : [];
    
    return {
      success: true,
      total: response.data.total || paymentMethods.length,
      data: paymentMethods
    };
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch payment method list',
    data: []
  };
};

/**
 * Process tax code list response
 * @param {Object} response - API response from tax code list endpoint
 * @returns {Object} Formatted tax code list
 */
export const taxCodeListGet = (response) => {
  if (response && response.data && response.data.success === true) {
    const item = response.data.data || {};
    return {
      success: true,
      data: item
    };
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch tax code list',
    data: {}
  };
};

/**
 * Process discount code list response
 * @param {Object} response - API response from discount code list endpoint
 * @returns {Object} Formatted discount code list
 */
export const discountCodeListGet = (response) => {
  if (response && response.data && response.data.success === true) {
    const item = response.data.data || {};
    return {
      success: true,
      data: item
    };
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch discount code list',
    data: {}
  };
};
